// src/pages/Reports/SlotOccupancyReportPage.js
//
// Slot Occupancy / Slot Performance Report UI
// ===========================================
//
// PURPOSE
// -------
// This page shows management how strongly individual opening-hour slots
// are used during the selected period.
//
// MAIN BUSINESS QUESTIONS
// -----------------------
// - Which hours are the busiest from a BUSINESS perspective?
// - Which hours are weak?
// - Which hours are practically dead?
// - Which slots generate the most business booked minutes?
// - Which slots generate the most allocated revenue?
// - Which slots are strongest salon-wide?
// - Which slots are strongest per active employee?
// - How much time is occupied by technical blocking such as breaks?
//
// IMPORTANT INTERPRETATION NOTE
// -----------------------------
// This page visualizes salon-wide demand / booked utilization based on:
// - completed visits
// - checked visits
// - overlap of visit duration with hourly slots
//
// It is not a perfect absolute-capacity model, because the system still does not
// know the full historical operational capacity of the salon in a complete way.
// However, it is already very strong for decision support because it combines:
// - business booked minutes
// - technical blocked minutes
// - business visit counts
// - allocated revenue
// - activity stability
// - active employee normalization
//
// IMPORTANT BACKEND EVOLUTION
// ---------------------------
// The backend now separates slot usage into TWO different time families:
//
// 1) Business activity
//    - real client service work
//    - contributes to demand, revenue, and visit counts
//
// 2) Technical blocking
//    - technical schedule entries such as "PRZERWA"
//    - consumes slot time
//    - does NOT contribute to business demand or revenue
//
// This is a very important improvement because it prevents business metrics
// from being distorted by technical calendar entries while still showing that
// slot time was not actually free.
//
// CURRENT BACKEND SHAPE
// ---------------------
// The current backend returns a richer response including:
//
// {
//   period,
//   filters,
//   methodology,
//   opening_schedule,
//   summary,
//   slot_summary,
//   heatmap_matrix,
//   top_slots,
//   weak_slots,
//   top_slots_by_revenue,
//   top_slots_by_revenue_per_booked_hour,
//   top_slots_per_active_employee
// }
//
// DATE FILTER FRAMEWORK
// ---------------------
// This page uses the shared evaluation-period framework,
// just like the other report pages.
//
// SERVICE INTEGRATION
// -------------------
// This page imports fetchSlotOccupancyReport from the shared reportService.
//
// UI GOALS
// --------
// 1) Keep the page visually aligned with the rest of the reporting pages.
// 2) Show both salon-wide and per-active-employee perspectives.
// 3) Keep ranking tables understandable without forcing business users to
//    reverse-engineer formulas from raw numbers.
// 4) Expose the backend recommendation as a management hint, not as an
//    automatic business truth.
// 5) Preserve local page scrolling and stable filter-toolbar behavior.
// 6) Show technical blocked time explicitly, without mixing it into revenue
//    or business demand.
// 7) Add visual chart components that make slot patterns readable in seconds.
//
// VISUALIZATION STRATEGY
// ----------------------
// This page now uses multiple chart blocks:
//
// 1) Slot Mix Overview
//    - slot-based chart
//    - switchable between combined / minutes only / revenue only
//
// 2) Weekday Business vs Technical vs Revenue
//    - weekday rollup
//    - switchable between absolute and normalized/open-slot mode
//
// 3) Top Revenue Slots
//    - fast economic ranking
//
// 4) Top Per-Active-Employee Slots
//    - operational efficiency reading
//
// IMPORTANT
// ---------
// These charts are intentionally managerial rather than overly analytical.
// The detailed table and slot cards still remain the source of drill-down detail.
//

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../../context/AuthContext";
import { fetchUsers } from "../../services/userService";
import { fetchSlotOccupancyReport } from "../../services/reportService";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Line,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
} from "recharts";

import "./SlotOccupancyReportPage.css";

// I18N NOTE
// ---------
// This page keeps backend metric fields, ranking modes, chart data keys, and
// heat classifications stable. The React layer translates frontend-owned
// headings, labels, chart series names, table headers, empty states, and local
// fallback strings. Backend methodology note_keys are translated by the
// frontend, while the legacy backend notes array remains a safe fallback for
// older payloads. Slot-level recommendations remain backend-owned payload text.

/* -------------------------------------------------------------------------- */
/* Chart palette                                                              */
/* -------------------------------------------------------------------------- */

/**
 * A stable local palette for this page.
 *
 * Meaning is intentionally consistent:
 * - business time / demand family: blue
 * - technical blocking: amber
 * - revenue / secondary line family: dark blue or green
 * - neutral frame / grid: grays
 */
const CHART_COLORS = Object.freeze({
  business: "#2563eb",
  businessLight: "#60a5fa",
  technical: "#f59e0b",
  technicalLight: "#fbbf24",
  revenue: "#1d4ed8",
  perEmployee: "#16a34a",
  grid: "#e5e7eb",
  axis: "#6b7280",
  tooltipBg: "#ffffff",
  tooltipBorder: "#d1d5db",
});

/* -------------------------------------------------------------------------- */
/* Small formatting helpers                                                   */
/* -------------------------------------------------------------------------- */

function formatMinutes(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0.0";
  return x.toFixed(1);
}

function formatNumber(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0.00";
  return x.toFixed(2);
}

function formatMoney(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0.00";
  return x.toFixed(2);
}

function formatPercentValue(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0.0%";
  return `${x.toFixed(1)}%`;
}

function formatPercentFromRatio(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0.0%";
  return `${(x * 100).toFixed(1)}%`;
}

function formatSlotLabel(slot) {
  if (!slot) return "—";
  return slot.slot_key || `${slot.slot_start || "?"}-${slot.slot_end || "?"}`;
}

function formatSlotSummaryText(
  slot,
  t,
  metric = "avg_business_booked_minutes_per_open_day"
) {
  if (!slot) return "—";

  if (metric === "avg_revenue_per_open_day") {
    return `${formatSlotLabel(slot)} • ${formatMoney(
      slot.avg_revenue_per_open_day
    )} ${t("reports:slotOccupancy.units.plnPerDay")}`;
  }

  if (metric === "revenue_per_booked_hour") {
    return `${formatSlotLabel(slot)} • ${formatMoney(
      slot.revenue_per_booked_hour
    )} ${t("reports:slotOccupancy.units.plnPerHour")}`;
  }

  if (metric === "avg_booked_minutes_per_active_employee") {
    return `${formatSlotLabel(slot)} • ${formatMinutes(
      slot.avg_booked_minutes_per_active_employee
    )} ${t("reports:slotOccupancy.units.minPerActiveEmp")}`;
  }

  if (metric === "avg_technical_blocked_minutes_per_open_day") {
    return `${formatSlotLabel(slot)} • ${formatMinutes(
      slot.avg_technical_blocked_minutes_per_open_day
    )} ${t("reports:slotOccupancy.units.techMinPerDay")}`;
  }

  return `${formatSlotLabel(slot)} • ${formatMinutes(
    slot.avg_business_booked_minutes_per_open_day
  )} ${t("reports:slotOccupancy.units.businessMinPerDay")}`;
}

function getHeatCellStyle(cell) {
  if (!cell?.is_open) {
    return {
      background: "#f3f4f6",
      color: "#9ca3af",
      borderColor: "#e5e7eb",
    };
  }

  const score = Math.max(0, Math.min(100, Number(cell?.heat_score) || 0));
  const alpha = 0.08 + (score / 100) * 0.55;

  return {
    background: `rgba(37, 99, 235, ${alpha})`,
    color: score >= 70 ? "#0f172a" : "#111827",
    borderColor: "rgba(37, 99, 235, 0.18)",
  };
}

function formatTopList(items) {
  if (!Array.isArray(items) || items.length === 0) return "—";
  return items.map((x) => `${x.name} (${x.count})`).join(", ");
}

function formatRecommendation(text, t) {
  const raw = String(text || "").trim();
  return raw || t("reports:slotOccupancy.fallbacks.noRecommendationAvailable");
}

/**
 * Generic tooltip formatter used by mixed slot charts.
 */
function formatChartTooltipValue(value, name, t) {
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return [value, name];
  }

  if (
    name === t("reports:slotOccupancy.chart.revenueOpenDay") ||
    name === t("reports:slotOccupancy.chart.revenueDay") ||
    name === t("reports:slotOccupancy.chart.revenueActiveEmployee")
  ) {
    return [`${formatMoney(numeric)} PLN`, name];
  }

  if (
    name === t("reports:slotOccupancy.chart.businessOpenDay") ||
    name === t("reports:slotOccupancy.chart.technicalOpenDay") ||
    name === t("reports:slotOccupancy.chart.bookedActiveEmployee")
  ) {
    return [`${formatMinutes(numeric)} min`, name];
  }

  return [formatNumber(numeric), name];
}

/**
 * Dedicated weekday-chart helpers.
 *
 * We keep them separate because weekday chart legend names change depending
 * on mode, and revenue must never be formatted as minutes.
 */
function getWeekdayRevenueSeriesLabel(mode, t) {
  return mode === "normalized"
    ? t("reports:slotOccupancy.chart.revenueOpenSlot")
    : t("reports:slotOccupancy.chart.revenueWeekday");
}

function getWeekdayBusinessSeriesLabel(mode, t) {
  return mode === "normalized"
    ? t("reports:slotOccupancy.chart.businessOpenSlot")
    : t("reports:slotOccupancy.chart.businessWeekday");
}

function getWeekdayTechnicalSeriesLabel(mode, t) {
  return mode === "normalized"
    ? t("reports:slotOccupancy.chart.technicalOpenSlot")
    : t("reports:slotOccupancy.chart.technicalWeekday");
}

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const SlotOccupancyReportPage = () => {
  const { t } = useTranslation(["common", "reports"]);
  const { authState } = useContext(AuthContext);

  const currentRole = authState?.user?.role || "";
  const currentUserId = authState?.user?.id;

  /**
   * Role-based allowed evaluation modes.
   */
  const allowedEvaluationModes = useMemo(() => {
    if (currentRole === "user") {
      return [EVALUATION_PERIOD_TYPES.MONTH];
    }

    return [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ];
  }, [currentRole, t]);

  /**
   * Shared evaluation-period controller.
   */
  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: allowedEvaluationModes,
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  /**
   * Local page filters.
   */
  const [filters, setFilters] = useState(() => ({
    userIds:
      currentRole === "user" && currentUserId != null ? [currentUserId] : [],
    rankingView: "salon_wide",

    /**
     * Weekday chart mode
     * ------------------
     * absolute   = summed weekday totals across open slots
     * normalized = average per open slot for that weekday
     */
    weekdayChartMode: "absolute",

    /**
     * Slot mix chart mode
     * -------------------
     * minutes_revenue = stacked minutes + revenue line
     * minutes_only    = stacked minutes only
     * revenue_only    = revenue bars only
     */
    slotMixChartMode: "minutes_revenue",
  }));

  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [loadedPeriodMeta, setLoadedPeriodMeta] = useState({
    from: selectedFrom,
    to: selectedTo,
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  /* ------------------------------------------------------------------------ */
  /* Keep userIds aligned for user role                                       */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (currentRole === "user" && currentUserId != null) {
      setFilters((prev) => ({
        ...prev,
        userIds: [currentUserId],
      }));
    }
  }, [currentRole, currentUserId]);

  /* ------------------------------------------------------------------------ */
  /* Load users for employee filter                                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsers();
        setUsers(Array.isArray(userList) ? userList : []);
      } catch (err) {
        console.error("[SlotOccupancyReportPage] ❌ Failed to fetch users:", err);
        setError(t("reports:slotOccupancy.errors.failedToLoadUsers"));
      }
    };

    if (currentRole !== "user") {
      loadUsers();
    }
  }, [currentRole, t]);

  /* ------------------------------------------------------------------------ */
  /* Load report                                                              */
  /* ------------------------------------------------------------------------ */

  const loadReportByRange = useCallback(
    async ({ from, to, userIds, displayLabel }) => {
      if (!from || !to) {
        setError(t("reports:common.invalidEvaluationPeriod"));
        return;
      }

      setLoading(true);
      setError("");

      try {
        const payload = await fetchSlotOccupancyReport(from, to, userIds);
        setReport(payload || null);

        setLoadedPeriodMeta({
          from,
          to,
          displayLabel: displayLabel || "",
        });
      } catch (err) {
        console.error(
          "[SlotOccupancyReportPage] ❌ Error fetching report:",
          err
        );
        setError(t("reports:slotOccupancy.errors.failedToFetch"));
        setReport(null);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  /**
   * Initial auto-load.
   */
  const initialRequestRef = useRef({
    from: selectedFrom,
    to: selectedTo,
    userIds:
      currentRole === "user" && currentUserId != null ? [currentUserId] : [],
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  useEffect(() => {
    loadReportByRange(initialRequestRef.current);
  }, [loadReportByRange]);

  /* ------------------------------------------------------------------------ */
  /* Derived data                                                             */
  /* ------------------------------------------------------------------------ */

  const summary = report?.summary || {};
  const methodologyNotes = useMemo(() => {
    const methodology = report?.methodology || {};
    const reportKind = methodology.report_kind || "slot_occupancy_demand";

    /**
     * Preferred path for the i18n migration:
     * backend sends stable note_keys, frontend owns the visible language.
     * The legacy notes array remains supported so this page continues to work
     * against older backend payloads during staged deployments.
     */
    if (Array.isArray(methodology.note_keys) && methodology.note_keys.length > 0) {
      return methodology.note_keys.map((noteKey) => {
        const translatedNote = t(
          `reports:methodology.${reportKind}.notes.${noteKey}`,
          { defaultValue: "" }
        );

        return translatedNote || String(noteKey);
      });
    }

    return Array.isArray(methodology.notes) ? methodology.notes : [];
  }, [report, t]);

  const slotSummary = Array.isArray(report?.slot_summary)
    ? report.slot_summary
    : [];
  const heatmapMatrix = Array.isArray(report?.heatmap_matrix)
    ? report.heatmap_matrix
    : [];
  const slotDefinitions = Array.isArray(report?.opening_schedule?.slot_definitions)
    ? report.opening_schedule.slot_definitions
    : [];

  const topSlots = Array.isArray(report?.top_slots) ? report.top_slots : [];
  const weakSlots = Array.isArray(report?.weak_slots) ? report.weak_slots : [];
  const topSlotsByRevenue = Array.isArray(report?.top_slots_by_revenue)
    ? report.top_slots_by_revenue
    : [];
  const topSlotsByRevenuePerBookedHour = Array.isArray(
    report?.top_slots_by_revenue_per_booked_hour
  )
    ? report.top_slots_by_revenue_per_booked_hour
    : [];
  const topSlotsPerActiveEmployee = Array.isArray(
    report?.top_slots_per_active_employee
  )
    ? report.top_slots_per_active_employee
    : [];

  const hasReportData = Boolean(report);

  /**
   * Fixed user display for role === "user".
   */
  const currentUserDisplay = useMemo(() => {
    const firstName = authState?.user?.first_name || "";
    const lastName = authState?.user?.last_name || "";
    const username = authState?.user?.username || "";

    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

    if (fullName && username) return `${fullName} (${username})`;
    if (fullName) return fullName;
    if (username) return username;

    return t("reports:common.currentUser", { defaultValue: "Current user" });
  }, [
    authState?.user?.first_name,
    authState?.user?.last_name,
    authState?.user?.username,
    t,
  ]);

  /**
   * Ranking table source selector.
   */
  const rankingRows = useMemo(() => {
    switch (filters.rankingView) {
      case "revenue":
        return [...slotSummary].sort(
          (a, b) =>
            Number(b.avg_revenue_per_open_day || 0) -
              Number(a.avg_revenue_per_open_day || 0) ||
            Number(a.slot_start_minutes || 0) -
              Number(b.slot_start_minutes || 0)
        );

      case "revenue_per_hour":
        return [...slotSummary].sort(
          (a, b) =>
            Number(b.revenue_per_booked_hour || 0) -
              Number(a.revenue_per_booked_hour || 0) ||
            Number(a.slot_start_minutes || 0) -
              Number(b.slot_start_minutes || 0)
        );

      case "per_active_employee":
        return [...slotSummary].sort(
          (a, b) =>
            Number(b.avg_booked_minutes_per_active_employee || 0) -
              Number(a.avg_booked_minutes_per_active_employee || 0) ||
            Number(a.slot_start_minutes || 0) -
              Number(b.slot_start_minutes || 0)
        );

      case "salon_wide":
      default:
        return [...slotSummary].sort(
          (a, b) =>
            Number(b.avg_business_booked_minutes_per_open_day || 0) -
              Number(a.avg_business_booked_minutes_per_open_day || 0) ||
            Number(a.slot_start_minutes || 0) -
              Number(b.slot_start_minutes || 0)
        );
    }
  }, [slotSummary, filters.rankingView]);

  /**
   * Slot-based chart data in time order.
   */
  const slotMixChartData = useMemo(() => {
    return [...slotSummary]
      .sort(
        (a, b) =>
          Number(a.slot_start_minutes || 0) - Number(b.slot_start_minutes || 0)
      )
      .map((row) => ({
        label: row.slot_key,
        businessMinutes:
          Number(row.avg_business_booked_minutes_per_open_day) || 0,
        technicalMinutes:
          Number(row.avg_technical_blocked_minutes_per_open_day) || 0,
        revenuePerDay: Number(row.avg_revenue_per_open_day) || 0,
      }));
  }, [slotSummary]);

  /**
   * Top revenue chart data.
   */
  const topRevenueChartData = useMemo(() => {
    return [...topSlotsByRevenue]
      .slice(0, 10)
      .map((row) => ({
        label: row.slot_key,
        revenuePerDay: Number(row.avg_revenue_per_open_day) || 0,
      }))
      .reverse();
  }, [topSlotsByRevenue]);

  /**
   * Top per-active-employee chart data.
   */
  const perActiveEmployeeChartData = useMemo(() => {
    return [...topSlotsPerActiveEmployee]
      .slice(0, 10)
      .map((row) => ({
        label: row.slot_key,
        bookedPerEmployee:
          Number(row.avg_booked_minutes_per_active_employee) || 0,
      }))
      .reverse();
  }, [topSlotsPerActiveEmployee]);

  /**
   * Weekday-level rollup derived from heatmap_matrix.
   *
   * absolute:
   * - sums values across open slots inside the weekday
   *
   * normalized:
   * - divides weekday sums by number of open slots
   */
  const weekdayChartData = useMemo(() => {
    return heatmapMatrix.map((row) => {
      const openCells = Array.isArray(row?.cells)
        ? row.cells.filter((cell) => cell?.is_open)
        : [];

      const openSlotCount = openCells.length || 0;

      const businessMinutesTotal = openCells.reduce(
        (sum, cell) =>
          sum + Number(cell?.avg_business_booked_minutes_per_open_day || 0),
        0
      );

      const technicalMinutesTotal = openCells.reduce(
        (sum, cell) =>
          sum + Number(cell?.avg_technical_blocked_minutes_per_open_day || 0),
        0
      );

      const revenuePerDayTotal = openCells.reduce(
        (sum, cell) => sum + Number(cell?.avg_revenue_per_open_day || 0),
        0
      );

      const divisor =
        filters.weekdayChartMode === "normalized" && openSlotCount > 0
          ? openSlotCount
          : 1;

      return {
        label: row?.weekday_label || row?.weekday || "—",
        businessMinutes: businessMinutesTotal / divisor,
        technicalMinutes: technicalMinutesTotal / divisor,
        revenuePerDay: revenuePerDayTotal / divisor,
        openSlotCount,
      };
    });
  }, [heatmapMatrix, filters.weekdayChartMode]);

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="slot-occupancy-page">
      <div className="slot-occupancy-header">
        <div className="header-left">
          <h1>{t("reports:slotOccupancy.title")}</h1>
          <p className="header-hint">{t("reports:slotOccupancy.headerHint")}</p>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="filters-bar">
        <div className="filters-left">
          <EvaluationPeriodPicker period={evaluationPeriod} />

          {currentRole !== "user" ? (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employees")}</label>

              <select
                multiple
                className="user-dropdown"
                value={filters.userIds}
                onChange={(e) => {
                  const selectedValues = Array.from(
                    e.target.selectedOptions,
                    (o) => Number(o.value)
                  ).filter(Number.isFinite);

                  setFilters((prev) => ({
                    ...prev,
                    userIds: selectedValues,
                  }));
                }}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.username})
                  </option>
                ))}
              </select>

              <div className="filter-help">{t("reports:common.multiSelectHelp")}</div>
            </div>
          ) : (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employee")}</label>
              <input
                type="text"
                className="user-fixed"
                value={currentUserDisplay}
                readOnly
              />
            </div>
          )}

          <div className="filter-item filter-item-ranking-view">
            <label className="filter-label">{t("reports:slotOccupancy.rankingView")}</label>
            <select
              className="ranking-view-select"
              value={filters.rankingView}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  rankingView: e.target.value,
                }))
              }
            >
              <option value="salon_wide">{t("reports:slotOccupancy.rankingViews.salonWide")}</option>
              <option value="revenue">{t("reports:slotOccupancy.rankingViews.revenue")}</option>
              <option value="revenue_per_hour">{t("reports:slotOccupancy.rankingViews.revenuePerHour")}</option>
              <option value="per_active_employee">{t("reports:slotOccupancy.rankingViews.perActiveEmployee")}</option>
            </select>
          </div>
        </div>

        <div className="filters-actions">
          <button
            className="apply-button"
            onClick={() =>
              loadReportByRange({
                from: selectedFrom,
                to: selectedTo,
                userIds: filters.userIds,
                displayLabel: evaluationPeriod.displayLabel,
              })
            }
            disabled={loading}
          >
            {loading ? t("reports:common.loading") : t("reports:common.generate")}
          </button>
        </div>
      </div>

      <div className="report-scroll">
        {loading ? (
          <div className="loading-panel">{t("reports:common.loading")}</div>
        ) : hasReportData ? (
          <>
            <div className="summary-grid">
              <div className="summary-card">
                <span className="summary-label">{t("reports:slotOccupancy.summary.loadedRows")}</span>
                <span className="summary-value">
                  {summary?.completed_rows_count ?? 0}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:slotOccupancy.summary.analyzedDays")}</span>
                <span className="summary-value">
                  {summary?.analyzed_days_total ?? 0}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:slotOccupancy.summary.openDays")}</span>
                <span className="summary-value">
                  {summary?.open_days_total ?? 0}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:slotOccupancy.summary.totalBusinessBookedMinutes")}</span>
                <span className="summary-value">
                  {formatMinutes(summary?.total_business_booked_minutes ?? 0)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">
                  {t("reports:slotOccupancy.summary.totalTechnicalBlockedMinutes")}
                </span>
                <span className="summary-value">
                  {formatMinutes(summary?.total_technical_blocked_minutes ?? 0)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:slotOccupancy.summary.totalBlockedMinutes")}</span>
                <span className="summary-value">
                  {formatMinutes(summary?.total_blocked_minutes ?? 0)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:slotOccupancy.summary.totalAllocatedRevenue")}</span>
                <span className="summary-value">
                  {formatMoney(summary?.total_allocated_revenue ?? 0)} PLN
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:slotOccupancy.summary.busiestBusinessSlot")}</span>
                <span className="summary-value summary-value--small">
                  {formatSlotSummaryText(summary?.busiest_slot, t)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:slotOccupancy.summary.strongestRevenueSlot")}</span>
                <span className="summary-value summary-value--small">
                  {formatSlotSummaryText(
                    summary?.strongest_revenue_slot,
                    t,
                    "avg_revenue_per_open_day"
                  )}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:slotOccupancy.summary.bestPlnPerBookedHour")}</span>
                <span className="summary-value summary-value--small">
                  {formatSlotSummaryText(
                    summary?.strongest_hourly_revenue_slot,
                    t,
                    "revenue_per_booked_hour"
                  )}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:slotOccupancy.summary.bestPerActiveEmployee")}</span>
                <span className="summary-value summary-value--small">
                  {formatSlotSummaryText(
                    summary?.strongest_per_active_employee_slot,
                    t,
                    "avg_booked_minutes_per_active_employee"
                  )}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">
                  {t("reports:slotOccupancy.summary.weakestOpenBusinessSlot")}
                </span>
                <span className="summary-value summary-value--small">
                  {formatSlotSummaryText(summary?.weakest_open_slot, t)}
                </span>
              </div>
            </div>

            <div className="methodology-card">
              <div className="methodology-title">{t("reports:slotOccupancy.methodology")}</div>
              <ul className="methodology-list">
                {methodologyNotes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>

              {(loadedPeriodMeta?.from || loadedPeriodMeta?.to) && (
                <div className="loaded-period-note">
                  {t("reports:common.loadedPeriod")}: <strong>{loadedPeriodMeta.from}</strong> →{" "}
                  <strong>{loadedPeriodMeta.to}</strong>
                  {loadedPeriodMeta.displayLabel ? (
                    <> ({loadedPeriodMeta.displayLabel})</>
                  ) : null}
                </div>
              )}
            </div>

            <div className="charts-grid">
              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <div className="chart-card__headerRow">
                    <div>
                      <h2>{t("reports:slotOccupancy.charts.slotMixOverviewTitle")}</h2>
                      <p>{t("reports:slotOccupancy.charts.slotMixOverviewDescription")}</p>
                    </div>

                    <div className="chart-mode-switch">
                      <label className="chart-mode-switch__label">{t("reports:slotOccupancy.view")}</label>
                      <select
                        className="chart-mode-switch__select"
                        value={filters.slotMixChartMode}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            slotMixChartMode: e.target.value,
                          }))
                        }
                      >
                        <option value="minutes_revenue">{t("reports:slotOccupancy.chartModes.minutesRevenue")}</option>
                        <option value="minutes_only">{t("reports:slotOccupancy.chartModes.minutesOnly")}</option>
                        <option value="revenue_only">{t("reports:slotOccupancy.chartModes.revenueOnly")}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    {filters.slotMixChartMode === "minutes_only" ? (
                      <BarChart
                        data={slotMixChartData}
                        margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                      >
                        <CartesianGrid
                          stroke={CHART_COLORS.grid}
                          strokeDasharray="3 3"
                        />
                        <XAxis
                          dataKey="label"
                          tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        />
                        <YAxis
                          tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        />
                        <Tooltip
                          formatter={(value, name) => {
                            const numeric = Number(value);
                            if (!Number.isFinite(numeric)) return [value, name];
                            return [`${formatMinutes(numeric)} min`, name];
                          }}
                          contentStyle={{
                            backgroundColor: CHART_COLORS.tooltipBg,
                            border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                            borderRadius: 8,
                          }}
                        />
                        <Legend />
                        <Bar
                          dataKey="businessMinutes"
                          name={t("reports:slotOccupancy.chart.businessOpenDay")}
                          stackId="slotTime"
                          fill={CHART_COLORS.business}
                          radius={[6, 6, 0, 0]}
                        />
                        <Bar
                          dataKey="technicalMinutes"
                          name={t("reports:slotOccupancy.chart.technicalOpenDay")}
                          stackId="slotTime"
                          fill={CHART_COLORS.technical}
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    ) : filters.slotMixChartMode === "revenue_only" ? (
                      <BarChart
                        data={slotMixChartData}
                        margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                      >
                        <CartesianGrid
                          stroke={CHART_COLORS.grid}
                          strokeDasharray="3 3"
                        />
                        <XAxis
                          dataKey="label"
                          tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        />
                        <YAxis
                          tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        />
                        <Tooltip
                          formatter={(value) => [
                            `${formatMoney(value)} PLN`,
                            t("reports:slotOccupancy.chart.revenueOpenDay"),
                          ]}
                          contentStyle={{
                            backgroundColor: CHART_COLORS.tooltipBg,
                            border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                            borderRadius: 8,
                          }}
                        />
                        <Legend />
                        <Bar
                          dataKey="revenuePerDay"
                          name={t("reports:slotOccupancy.chart.revenueOpenDay")}
                          radius={[6, 6, 0, 0]}
                        >
                          {slotMixChartData.map((entry, index) => (
                            <Cell
                              key={`slotmix-revenue-${index}`}
                              fill={CHART_COLORS.revenue}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    ) : (
                      <ComposedChart
                        data={slotMixChartData}
                        margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                      >
                        <CartesianGrid
                          stroke={CHART_COLORS.grid}
                          strokeDasharray="3 3"
                        />
                        <XAxis
                          dataKey="label"
                          tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        />
                        <YAxis
                          yAxisId="left"
                          tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        />
                        <Tooltip
                          formatter={(value, name) => formatChartTooltipValue(value, name, t)}
                          contentStyle={{
                            backgroundColor: CHART_COLORS.tooltipBg,
                            border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                            borderRadius: 8,
                          }}
                        />
                        <Legend />
                        <Bar
                          yAxisId="left"
                          dataKey="businessMinutes"
                          name={t("reports:slotOccupancy.chart.businessOpenDay")}
                          stackId="slotTime"
                          fill={CHART_COLORS.business}
                          radius={[6, 6, 0, 0]}
                        />
                        <Bar
                          yAxisId="left"
                          dataKey="technicalMinutes"
                          name={t("reports:slotOccupancy.chart.technicalOpenDay")}
                          stackId="slotTime"
                          fill={CHART_COLORS.technical}
                          radius={[6, 6, 0, 0]}
                        />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="revenuePerDay"
                          name={t("reports:slotOccupancy.chart.revenueOpenDay")}
                          stroke={CHART_COLORS.revenue}
                          strokeWidth={2}
                          dot={{ r: 2, fill: CHART_COLORS.revenue }}
                          activeDot={{ r: 5, fill: CHART_COLORS.revenue }}
                        />
                      </ComposedChart>
                    )}
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <div className="chart-card__headerRow">
                    <div>
                      <h2>{t("reports:slotOccupancy.charts.weekdayBusinessTitle")}</h2>
                      <p>{t("reports:slotOccupancy.charts.weekdayBusinessDescription")}</p>
                    </div>

                    <div className="chart-mode-switch">
                      <label className="chart-mode-switch__label">{t("reports:slotOccupancy.view")}</label>
                      <select
                        className="chart-mode-switch__select"
                        value={filters.weekdayChartMode}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            weekdayChartMode: e.target.value,
                          }))
                        }
                      >
                        <option value="absolute">{t("reports:slotOccupancy.chartModes.absolute")}</option>
                        <option value="normalized">{t("reports:slotOccupancy.chartModes.normalized")}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={weekdayChartData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid
                        stroke={CHART_COLORS.grid}
                        strokeDasharray="3 3"
                      />

                      <XAxis
                        dataKey="label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="left"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <Tooltip
                        formatter={(value, name) => {
                          const numeric = Number(value);

                          if (!Number.isFinite(numeric)) {
                            return [value, name];
                          }

                          const revenueLabel = getWeekdayRevenueSeriesLabel(
                            filters.weekdayChartMode,
                            t
                          );

                          if (name === revenueLabel) {
                            return [
                              filters.weekdayChartMode === "normalized"
                                ? `${formatMoney(numeric)} ${t("reports:slotOccupancy.chart.revenueOpenSlot")}`
                                : `${formatMoney(numeric)} PLN`,
                              name,
                            ];
                          }

                          return [
                            filters.weekdayChartMode === "normalized"
                              ? `${formatMinutes(numeric)} ${t("reports:slotOccupancy.chart.businessOpenSlot")}`
                              : `${formatMinutes(numeric)} min`,
                            name,
                          ];
                        }}
                        labelFormatter={(label, payload) => {
                          const row = payload?.[0]?.payload;
                          const openSlots = row?.openSlotCount ?? 0;
                          const modeText =
                            filters.weekdayChartMode === "normalized"
                              ? t("reports:slotOccupancy.chartModes.normalized")
                              : t("reports:slotOccupancy.chartModes.absolute");

                          return `${label} | ${modeText} | ${t("reports:slotOccupancy.labels.openDays")}: ${openSlots}`;
                        }}
                        contentStyle={{
                          backgroundColor: CHART_COLORS.tooltipBg,
                          border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                          borderRadius: 8,
                        }}
                      />

                      <Legend />

                      <Bar
                        yAxisId="left"
                        dataKey="businessMinutes"
                        name={getWeekdayBusinessSeriesLabel(
                          filters.weekdayChartMode,
                          t
                        )}
                        stackId="weekdayTime"
                        fill={CHART_COLORS.business}
                        radius={[6, 6, 0, 0]}
                      />

                      <Bar
                        yAxisId="left"
                        dataKey="technicalMinutes"
                        name={getWeekdayTechnicalSeriesLabel(
                          filters.weekdayChartMode,
                          t
                        )}
                        stackId="weekdayTime"
                        fill={CHART_COLORS.technical}
                        radius={[6, 6, 0, 0]}
                      />

                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="revenuePerDay"
                        name={getWeekdayRevenueSeriesLabel(
                          filters.weekdayChartMode,
                          t
                        )}
                        stroke={CHART_COLORS.perEmployee}
                        strokeWidth={2}
                        dot={{ r: 3, fill: CHART_COLORS.perEmployee }}
                        activeDot={{ r: 5, fill: CHART_COLORS.perEmployee }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:slotOccupancy.charts.topRevenueSlotsTitle")}</h2>
                  <p>{t("reports:slotOccupancy.charts.topRevenueSlotsDescription")}</p>
                </div>

                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={topRevenueChartData}
                      layout="vertical"
                      margin={{ top: 10, right: 24, left: 24, bottom: 8 }}
                    >
                      <CartesianGrid
                        stroke={CHART_COLORS.grid}
                        strokeDasharray="3 3"
                      />
                      <XAxis
                        type="number"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={90}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value) => [
                          `${formatMoney(value)} PLN`,
                          t("reports:slotOccupancy.chart.revenueOpenDay"),
                        ]}
                        contentStyle={{
                          backgroundColor: CHART_COLORS.tooltipBg,
                          border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                          borderRadius: 8,
                        }}
                      />
                      <Legend />
                      <Bar
                        dataKey="revenuePerDay"
                        name={t("reports:slotOccupancy.chart.revenueOpenDay")}
                        radius={[0, 8, 8, 0]}
                      >
                        {topRevenueChartData.map((entry, index) => (
                          <Cell
                            key={`revenue-bar-${index}`}
                            fill={CHART_COLORS.business}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:slotOccupancy.charts.topPerActiveEmployeeTitle")}</h2>
                  <p>{t("reports:slotOccupancy.charts.topPerActiveEmployeeDescription")}</p>
                </div>

                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={perActiveEmployeeChartData}
                      layout="vertical"
                      margin={{ top: 10, right: 24, left: 24, bottom: 8 }}
                    >
                      <CartesianGrid
                        stroke={CHART_COLORS.grid}
                        strokeDasharray="3 3"
                      />
                      <XAxis
                        type="number"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={90}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value) => [
                          `${formatMinutes(value)} min`,
                          "Booked / Active Employee",
                        ]}
                        contentStyle={{
                          backgroundColor: CHART_COLORS.tooltipBg,
                          border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                          borderRadius: 8,
                        }}
                      />
                      <Legend />
                      <Bar
                        dataKey="bookedPerEmployee"
                        name={t("reports:slotOccupancy.chart.bookedActiveEmployee")}
                        radius={[0, 8, 8, 0]}
                      >
                        {perActiveEmployeeChartData.map((entry, index) => (
                          <Cell
                            key={`active-bar-${index}`}
                            fill={CHART_COLORS.perEmployee}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:slotOccupancy.charts.heatmapTitle")}</h2>
                <p>{t("reports:slotOccupancy.charts.heatmapDescription")}</p>
              </div>

              <div className="heatmap-wrapper">
                <table className="heatmap-table">
                  <thead>
                    <tr>
                      <th>{t("reports:slotOccupancy.labels.weekday")}</th>
                      {slotDefinitions.map((slot) => (
                        <th key={slot.slot_key}>{slot.slot_key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapMatrix.map((row) => (
                      <tr key={row.weekday}>
                        <td className="weekday-cell">{row.weekday_label}</td>

                        {row.cells.map((cell) => (
                          <td
                            key={`${row.weekday}-${cell.slot_key}`}
                            className={`heat-cell ${
                              !cell.is_open ? "heat-cell--closed" : ""
                            }`}
                            style={getHeatCellStyle(cell)}
                            title={
                              cell.is_open
                                ? `${row.weekday_label} ${cell.slot_key}
${t("reports:slotOccupancy.heatmapTooltip.businessBookedMinOpenDay")}: ${formatMinutes(
                                    cell.avg_business_booked_minutes_per_open_day
                                  )}
${t("reports:slotOccupancy.heatmapTooltip.technicalBlockedMinOpenDay")}: ${formatMinutes(
                                    cell.avg_technical_blocked_minutes_per_open_day
                                  )}
${t("reports:slotOccupancy.heatmapTooltip.totalBlockedMinOpenDay")}: ${formatMinutes(
                                    cell.avg_total_blocked_minutes_per_open_day
                                  )}
${t("reports:slotOccupancy.heatmapTooltip.businessVisitsOpenDay")}: ${formatNumber(
                                    cell.avg_business_visits_per_open_day
                                  )}
${t("reports:slotOccupancy.heatmapTooltip.avgRevenueOpenDay")}: ${formatMoney(
                                    cell.avg_revenue_per_open_day
                                  )} PLN
${t("reports:slotOccupancy.heatmapTooltip.avgActiveEmployeesOpenDay")}: ${formatNumber(
                                    cell.avg_active_employees_per_open_day
                                  )}
${t("reports:slotOccupancy.heatmapTooltip.bookedMinActiveEmployee")}: ${formatMinutes(
                                    cell.avg_booked_minutes_per_active_employee
                                  )}
${t("reports:slotOccupancy.heatmapTooltip.revenueActiveEmployee")}: ${formatMoney(
                                    cell.avg_revenue_per_active_employee
                                  )} PLN
${t("reports:slotOccupancy.heatmapTooltip.revenueBookedHour")}: ${formatMoney(
                                    cell.revenue_per_booked_hour
                                  )} PLN
${t("reports:slotOccupancy.heatmapTooltip.businessActivityRate")}: ${formatPercentFromRatio(
                                    cell.activity_rate
                                  )}
${t("reports:slotOccupancy.heatmapTooltip.technicalBlockRate")}: ${formatPercentFromRatio(
                                    cell.technical_block_rate
                                  )}
${t("reports:slotOccupancy.heatmapTooltip.businessUtilization")}: ${formatPercentValue(
                                    cell.business_utilization_percent
                                  )}
${t("reports:slotOccupancy.heatmapTooltip.technicalBlockedShare")}: ${formatPercentValue(
                                    cell.technical_blocked_share_percent
                                  )}
${t("reports:slotOccupancy.heatmapTooltip.totalBlockedShare")}: ${formatPercentValue(
                                    cell.total_blocked_share_percent
                                  )}
${t("reports:slotOccupancy.heatmapTooltip.topServices")}: ${formatTopList(cell.top_services)}
${t("reports:slotOccupancy.heatmapTooltip.topCategories")}: ${formatTopList(cell.top_categories)}
${t("reports:slotOccupancy.heatmapTooltip.recommendation")}: ${formatRecommendation(cell.recommendation, t)}`
                                : `${row.weekday_label} ${cell.slot_key}: ${t("reports:slotOccupancy.labels.closed")}`
                            }
                          >
                            {!cell.is_open ? (
                              <div className="heat-cell__closed">{t("reports:slotOccupancy.labels.closed")}</div>
                            ) : (
                              <>
                                <div className="heat-cell__minutes">
                                  {formatMinutes(
                                    cell.avg_business_booked_minutes_per_open_day
                                  )}
                                  m
                                </div>
                                <div className="heat-cell__meta">
                                  {formatMoney(cell.avg_revenue_per_open_day)} PLN
                                </div>
                                <div className="heat-cell__submeta">
                                  T{" "}
                                  {formatMinutes(
                                    cell.avg_technical_blocked_minutes_per_open_day
                                  )}
                                  m
                                </div>
                              </>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:slotOccupancy.charts.globalRankingTitle")}</h2>
                <p>{t("reports:slotOccupancy.charts.globalRankingDescription")}</p>
              </div>

              <div className="ranking-table-wrapper">
                {filters.rankingView === "salon_wide" && (
                  <table className="ranking-table">
                    <thead>
                      <tr>
                        <th>{t("reports:slotOccupancy.labels.slot")}</th>
                        <th>{t("reports:slotOccupancy.labels.openDays")}</th>
                        <th>{t("reports:slotOccupancy.labels.businessActivityRate")}</th>
                        <th>{t("reports:slotOccupancy.labels.technicalBlockRate")}</th>
                        <th>{t("reports:slotOccupancy.labels.avgBusinessMinOpenDay")}</th>
                        <th>{t("reports:slotOccupancy.labels.avgTechnicalMinOpenDay")}</th>
                        <th>{t("reports:slotOccupancy.labels.businessDemandIndex")}</th>
                        <th>{t("reports:slotOccupancy.labels.heatClass")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankingRows.map((row) => (
                        <tr key={row.slot_key}>
                          <td>{row.slot_key}</td>
                          <td>{row.open_days_count}</td>
                          <td>{formatPercentFromRatio(row.activity_rate)}</td>
                          <td>
                            {formatPercentFromRatio(row.technical_block_rate)}
                          </td>
                          <td>
                            {formatMinutes(
                              row.avg_business_booked_minutes_per_open_day
                            )}
                          </td>
                          <td>
                            {formatMinutes(
                              row.avg_technical_blocked_minutes_per_open_day
                            )}
                          </td>
                          <td>{formatNumber(row.business_demand_index)}</td>
                          <td>
                            <span
                              className={`heat-badge heat-badge--${row.heat_classification}`}
                            >
                              {row.heat_classification}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {filters.rankingView === "revenue" && (
                  <table className="ranking-table">
                    <thead>
                      <tr>
                        <th>{t("reports:slotOccupancy.labels.slot")}</th>
                        <th>{t("reports:slotOccupancy.labels.avgRevenueOpenDay")}</th>
                        <th>{t("reports:slotOccupancy.labels.totalAllocatedRevenue")}</th>
                        <th>{t("reports:slotOccupancy.labels.avgRevenueVisit")}</th>
                        <th>{t("reports:slotOccupancy.labels.topServices")}</th>
                        <th>{t("reports:slotOccupancy.labels.topCategories")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankingRows.map((row) => (
                        <tr key={row.slot_key}>
                          <td>{row.slot_key}</td>
                          <td>{formatMoney(row.avg_revenue_per_open_day)} PLN</td>
                          <td>{formatMoney(row.total_allocated_revenue)} PLN</td>
                          <td>{formatMoney(row.avg_revenue_per_visit)} PLN</td>
                          <td className="wrap-cell">
                            {formatTopList(row.top_services)}
                          </td>
                          <td className="wrap-cell">
                            {formatTopList(row.top_categories)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {filters.rankingView === "revenue_per_hour" && (
                  <table className="ranking-table">
                    <thead>
                      <tr>
                        <th>{t("reports:slotOccupancy.labels.slot")}</th>
                        <th>{t("reports:slotOccupancy.labels.revenueBookedHour")}</th>
                        <th>{t("reports:slotOccupancy.labels.avgRevenueOpenDay")}</th>
                        <th>{t("reports:slotOccupancy.labels.avgBusinessMinOpenDay")}</th>
                        <th>{t("reports:slotOccupancy.labels.businessActivityRate")}</th>
                        <th>{t("reports:slotOccupancy.labels.recommendation")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankingRows.map((row) => (
                        <tr key={row.slot_key}>
                          <td>{row.slot_key}</td>
                          <td>{formatMoney(row.revenue_per_booked_hour)} {t("reports:slotOccupancy.units.plnPerHour")}</td>
                          <td>{formatMoney(row.avg_revenue_per_open_day)} PLN</td>
                          <td>
                            {formatMinutes(
                              row.avg_business_booked_minutes_per_open_day
                            )}
                          </td>
                          <td>{formatPercentFromRatio(row.activity_rate)}</td>
                          <td className="wrap-cell">
                            {formatRecommendation(row.recommendation, t)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {filters.rankingView === "per_active_employee" && (
                  <table className="ranking-table">
                    <thead>
                      <tr>
                        <th>{t("reports:slotOccupancy.labels.slot")}</th>
                        <th>{t("reports:slotOccupancy.labels.avgActiveEmployeesOpenDay")}</th>
                        <th>{t("reports:slotOccupancy.labels.avgBookedMinActiveEmployee")}</th>
                        <th>{t("reports:slotOccupancy.labels.avgRevenueActiveEmployee")}</th>
                        <th>{t("reports:slotOccupancy.labels.businessActivityRate")}</th>
                        <th>{t("reports:slotOccupancy.labels.recommendation")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankingRows.map((row) => (
                        <tr key={row.slot_key}>
                          <td>{row.slot_key}</td>
                          <td>
                            {formatNumber(
                              row.avg_active_employees_per_open_day
                            )}
                          </td>
                          <td>
                            {formatMinutes(
                              row.avg_booked_minutes_per_active_employee
                            )}
                          </td>
                          <td>
                            {formatMoney(row.avg_revenue_per_active_employee)} PLN
                          </td>
                          <td>{formatPercentFromRatio(row.activity_rate)}</td>
                          <td className="wrap-cell">
                            {formatRecommendation(row.recommendation, t)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="ranking-grid">
              <div className="report-card">
                <div className="card-header">
                  <h2>{t("reports:slotOccupancy.charts.topStrongestBusinessSlots")}</h2>
                </div>

                <div className="mini-ranking-list">
                  {topSlots.map((slot) => (
                    <div
                      className="mini-ranking-item"
                      key={`top-${slot.slot_key}`}
                    >
                      <div className="mini-ranking-item__top">
                        <strong>{slot.slot_key}</strong>
                        <span>
                          {formatMinutes(
                            slot.avg_business_booked_minutes_per_open_day
                          )}
                          {t("reports:slotOccupancy.units.minutesShortPerDay")}
                        </span>
                      </div>

                      <div className="mini-ranking-bar">
                        <div
                          className="mini-ranking-bar__fill mini-ranking-bar__fill--strong"
                          style={{
                            width: `${Math.max(4, slot.heat_score || 0)}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="report-card">
                <div className="card-header">
                  <h2>{t("reports:slotOccupancy.charts.topWeakestOpenBusinessSlots")}</h2>
                </div>

                <div className="mini-ranking-list">
                  {weakSlots.map((slot) => (
                    <div
                      className="mini-ranking-item"
                      key={`weak-${slot.slot_key}`}
                    >
                      <div className="mini-ranking-item__top">
                        <strong>{slot.slot_key}</strong>
                        <span>
                          {formatMinutes(
                            slot.avg_business_booked_minutes_per_open_day
                          )}
                          {t("reports:slotOccupancy.units.minutesShortPerDay")}
                        </span>
                      </div>

                      <div className="mini-ranking-bar">
                        <div
                          className="mini-ranking-bar__fill mini-ranking-bar__fill--weak"
                          style={{
                            width: `${Math.max(4, slot.heat_score || 0)}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="ranking-grid ranking-grid--three">
              <div className="report-card">
                <div className="card-header">
                  <h2>{t("reports:slotOccupancy.charts.topByRevenue")}</h2>
                </div>

                <div className="mini-ranking-list">
                  {topSlotsByRevenue.map((slot) => (
                    <div
                      className="mini-ranking-item"
                      key={`revenue-${slot.slot_key}`}
                    >
                      <div className="mini-ranking-item__top">
                        <strong>{slot.slot_key}</strong>
                        <span>{formatMoney(slot.avg_revenue_per_open_day)} {t("reports:slotOccupancy.units.plnPerDay")}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="report-card">
                <div className="card-header">
                  <h2>{t("reports:slotOccupancy.charts.topByPlnBookedHour")}</h2>
                </div>

                <div className="mini-ranking-list">
                  {topSlotsByRevenuePerBookedHour.map((slot) => (
                    <div
                      className="mini-ranking-item"
                      key={`revhour-${slot.slot_key}`}
                    >
                      <div className="mini-ranking-item__top">
                        <strong>{slot.slot_key}</strong>
                        <span>{formatMoney(slot.revenue_per_booked_hour)} {t("reports:slotOccupancy.units.plnPerHour")}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="report-card">
                <div className="card-header">
                  <h2>{t("reports:slotOccupancy.charts.topPerActiveEmployee")}</h2>
                </div>

                <div className="mini-ranking-list">
                  {topSlotsPerActiveEmployee.map((slot) => (
                    <div
                      className="mini-ranking-item"
                      key={`active-${slot.slot_key}`}
                    >
                      <div className="mini-ranking-item__top">
                        <strong>{slot.slot_key}</strong>
                        <span>
                          {formatMinutes(slot.avg_booked_minutes_per_active_employee)} {t("reports:slotOccupancy.units.minPerEmp")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:slotOccupancy.charts.detailedSlotCardsTitle")}</h2>
                <p>{t("reports:slotOccupancy.charts.detailedSlotCardsDescription")}</p>
              </div>

              <div className="slot-card-grid">
                {slotSummary.map((slot) => (
                  <div className="slot-card" key={`slot-card-${slot.slot_key}`}>
                    <div className="slot-card__header">
                      <div className="slot-card__title">{slot.slot_key}</div>
                      <span
                        className={`heat-badge heat-badge--${slot.heat_classification}`}
                      >
                        {slot.heat_classification}
                      </span>
                    </div>

                    <div className="slot-card__metrics">
                      <div>
                        <span>{t("reports:slotOccupancy.labels.businessDay")}</span>
                        <strong>
                          {formatMinutes(
                            slot.avg_business_booked_minutes_per_open_day
                          )}{" "}
                          {t("reports:slotOccupancy.units.minPerDay")}
                        </strong>
                      </div>
                      <div>
                        <span>{t("reports:slotOccupancy.labels.technicalDay")}</span>
                        <strong>
                          {formatMinutes(
                            slot.avg_technical_blocked_minutes_per_open_day
                          )}{" "}
                          {t("reports:slotOccupancy.units.minPerDay")}
                        </strong>
                      </div>
                      <div>
                        <span>{t("reports:slotOccupancy.labels.totalBlockedDay")}</span>
                        <strong>
                          {formatMinutes(
                            slot.avg_total_blocked_minutes_per_open_day
                          )}{" "}
                          {t("reports:slotOccupancy.units.minPerDay")}
                        </strong>
                      </div>
                      <div>
                        <span>{t("reports:slotOccupancy.labels.revenueDay")}</span>
                        <strong>{formatMoney(slot.avg_revenue_per_open_day)} {t("reports:slotOccupancy.units.plnPerDay")}</strong>
                      </div>
                      <div>
                        <span>{t("reports:slotOccupancy.labels.revenueHour")}</span>
                        <strong>{formatMoney(slot.revenue_per_booked_hour)} {t("reports:slotOccupancy.units.plnPerHour")}</strong>
                      </div>
                      <div>
                        <span>{t("reports:slotOccupancy.labels.businessActivityRate")}</span>
                        <strong>{formatPercentFromRatio(slot.activity_rate)}</strong>
                      </div>
                      <div>
                        <span>{t("reports:slotOccupancy.labels.technicalBlockRate")}</span>
                        <strong>
                          {formatPercentFromRatio(slot.technical_block_rate)}
                        </strong>
                      </div>
                      <div>
                        <span>{t("reports:slotOccupancy.labels.avgActiveEmpDay")}</span>
                        <strong>
                          {formatNumber(slot.avg_active_employees_per_open_day)}
                        </strong>
                      </div>
                      <div>
                        <span>{t("reports:slotOccupancy.labels.bookedActiveEmp")}</span>
                        <strong>
                          {formatMinutes(
                            slot.avg_booked_minutes_per_active_employee
                          )}{" "}
                          {t("reports:slotOccupancy.units.minPerActiveEmp")}
                        </strong>
                      </div>
                      <div>
                        <span>{t("reports:slotOccupancy.labels.businessUtil")}</span>
                        <strong>
                          {formatPercentValue(slot.business_utilization_percent)}
                        </strong>
                      </div>
                      <div>
                        <span>{t("reports:slotOccupancy.labels.technicalShare")}</span>
                        <strong>
                          {formatPercentValue(
                            slot.technical_blocked_share_percent
                          )}
                        </strong>
                      </div>
                      <div>
                        <span>{t("reports:slotOccupancy.labels.totalBlockedShare")}</span>
                        <strong>
                          {formatPercentValue(slot.total_blocked_share_percent)}
                        </strong>
                      </div>
                    </div>

                    <div className="slot-card__lists">
                      <div>
                        <div className="slot-card__subhead">{t("reports:slotOccupancy.labels.topServices")}</div>
                        <div className="slot-card__text">
                          {formatTopList(slot.top_services)}
                        </div>
                      </div>

                      <div>
                        <div className="slot-card__subhead">{t("reports:slotOccupancy.labels.topCategories")}</div>
                        <div className="slot-card__text">
                          {formatTopList(slot.top_categories)}
                        </div>
                      </div>
                    </div>

                    <div className="slot-card__recommendation">
                      <div className="slot-card__subhead">{t("reports:slotOccupancy.labels.recommendation")}</div>
                      <div className="slot-card__text">
                        {formatRecommendation(slot.recommendation, t)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="empty-panel">{t("reports:common.noDataAvailable")}</div>
        )}
      </div>
    </div>
  );
};

export default SlotOccupancyReportPage;

